import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useActiveOrg } from '@/lib/activeOrg';
import { type MeResponse, useLogout } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { useRoles } from '@/lib/roles';

/**
 * User-name pulldown — Confluence/Jira-style settings entry point.
 *
 * Replaces the prior "email link to /profile + standalone Sign out
 * button" pair, plus absorbs the "Admin" main-nav link and the buried
 * "Organization settings…" footer of the org switcher. All three
 * settings destinations now live in this one menu, role-gated:
 *
 * - Personal settings (always)
 * - Organization administration (active-org admin/owner only)
 * - Site administration (is_superuser or system 'admin' role)
 *
 * The org switcher stays as a separate header dropdown — switching
 * orgs is a frequent scoping action, different cognitive task than
 * "open settings".
 */
export function UserMenu({ me }: { me: MeResponse }) {
  const logout = useLogout();
  const activeOrg = useActiveOrg(me.organizations);
  const { isSiteAdmin, isOrgAdmin } = useRoles();

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const close = () => setOpen(false);
  const triggerLabel = me.user.name?.trim() || me.user.email;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'rounded-md border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 px-3 py-1.5 text-sm',
          'hover:bg-stone-100 dark:hover:bg-stone-800',
          'dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-700',
        )}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="font-medium">{triggerLabel}</span>
        <span className="ml-1 text-stone-400 dark:text-stone-500">▾</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-72 rounded-md border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-md dark:border-stone-700 dark:bg-stone-900"
        >
          <div className="border-b border-stone-200 dark:border-stone-800 px-3 py-2">
            {me.user.name && (
              <p className="text-sm font-medium text-stone-800 dark:text-stone-100">
                {me.user.name}
              </p>
            )}
            <p className="truncate text-xs text-stone-500 dark:text-stone-400">
              {me.user.email}
            </p>
          </div>

          <ul className="py-1 text-sm">
            <li>
              <Link
                to="/profile"
                onClick={close}
                className="block px-3 py-2 text-stone-700 dark:text-stone-200 hover:bg-forge-50 dark:hover:bg-stone-800"
              >
                Personal settings
              </Link>
            </li>
            {isOrgAdmin && activeOrg && (
              <li>
                <Link
                  to={`/orgs/${activeOrg.slug}`}
                  onClick={close}
                  className="block px-3 py-2 text-stone-700 dark:text-stone-200 hover:bg-forge-50 dark:hover:bg-stone-800"
                >
                  Organization administration
                  <span className="ml-2 text-xs text-stone-500 dark:text-stone-400">
                    {activeOrg.name}
                  </span>
                </Link>
              </li>
            )}
            {isSiteAdmin && (
              <li>
                <Link
                  to="/admin"
                  onClick={close}
                  className="block px-3 py-2 text-forge-700 dark:text-forge-400 hover:bg-forge-50 dark:hover:bg-stone-800"
                >
                  Site administration
                </Link>
              </li>
            )}
          </ul>

          <div className="border-t border-stone-200 dark:border-stone-800 py-1">
            <button
              type="button"
              onClick={() => {
                close();
                logout.mutate();
              }}
              disabled={logout.isPending}
              className="block w-full px-3 py-2 text-left text-sm text-stone-700 dark:text-stone-200 hover:bg-forge-50 dark:hover:bg-stone-800 disabled:opacity-60"
            >
              {logout.isPending ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
